import { measuredRiskScore } from './measuredRiskScore';

type PortfolioClient = {
  company_name?: string;
  status?: string | null;
  completion_percentage?: number | string | null;
  risk_score?: number | string | null;
  annual_revenue?: number | string | null;
  latest_assessment?: { annual_revenue?: number | string | null } | null;
};

export type ClientRiskBadge =
  | { kind: 'score'; score: number }
  | { kind: 'status'; label: string }
  | { kind: 'not_measured' };

const progressOf = (c: PortfolioClient): number => {
  const n = Number(c.completion_percentage);
  return Number.isFinite(n) ? n : 0;
};

const isFinished = (c: PortfolioClient): boolean => c.status === 'completed';

const revenueOf = (c: PortfolioClient): number | null => {
  const raw = c.annual_revenue ?? c.latest_assessment?.annual_revenue;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * What a portfolio client's card shows where the risk score goes (Pat, 2026-10-19): an unfinished
 * client shows the status of its assessment, never a zero or empty score; a finished client shows
 * its score (the CRS), or "not measured" if there is none yet.
 */
export function clientRiskBadge(c: PortfolioClient): ClientRiskBadge {
  if (!isFinished(c)) {
    const pct = Math.round(progressOf(c));
    return pct > 0
      ? { kind: 'status', label: `Assessment in progress · ${pct}%` }
      : { kind: 'status', label: 'Not started' };
  }
  const score = measuredRiskScore(c);
  return score === null ? { kind: 'not_measured' } : { kind: 'score', score };
}

/**
 * Sort comparator for the portfolio list. A client without the sorted value — an unfinished
 * client when sorting by risk, an unanswered revenue when sorting by revenue — goes after every
 * client that has one, in either direction, and those are ordered by progress (furthest along
 * first). It used to read the missing value as 0, so an unfinished client sorted as the lowest risk.
 */
export function comparePortfolioClients(
  a: PortfolioClient,
  b: PortfolioClient,
  sortBy: string,
  order: 'asc' | 'desc'
): number {
  const dir = order === 'asc' ? 1 : -1;
  if (sortBy === 'status') return dir * (progressOf(a) - progressOf(b));
  if (sortBy === 'risk' || sortBy === 'revenue') {
    const valueOf = (c: PortfolioClient) => (sortBy === 'risk'
      ? (isFinished(c) ? measuredRiskScore(c) : null)
      : revenueOf(c));
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va === null && vb === null) return progressOf(b) - progressOf(a);
    if (va === null) return 1;
    if (vb === null) return -1;
    return dir * (va - vb);
  }
  return dir * (a.company_name || '').localeCompare(b.company_name || '');
}
